import { Trans, useLingui } from '@lingui/react/macro';
import { Button, toast } from '@utima/ui';
import { Copy, ExternalLink, X } from 'lucide-react';

import { Alert } from '@/components/Alert';

import type { CollectionMethodTarget } from './planManagementShared';
import { StatusBadge } from './StatusBadge';

export type AssignResultView = {
  invoiceId: string;
  invoiceStatus: string;
  hostedInvoiceUrl: string | null;
  hostedInvoiceUrlExpiresAt: string | null;
  collectionMethodTarget: CollectionMethodTarget;
  sent: boolean;
};

type AssignResultBannerProps = {
  result: AssignResultView | null;
  onDismiss: () => void;
};

export function AssignResultBanner({
  result,
  onDismiss,
}: AssignResultBannerProps) {
  const { t } = useLingui();

  if (!result) {
    return null;
  }

  const { hostedInvoiceUrl } = result;

  const copyLink = async () => {
    if (!hostedInvoiceUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(hostedInvoiceUrl);
      toast.success(t`Hosted invoice link copied.`);
    } catch {
      toast.error(t`Could not copy link.`);
    }
  };

  return (
    <Alert
      variant='success'
      title={<Trans>Plan assigned — subscription active</Trans>}
      className='mb-6'
      description={
        <div className='space-y-2 text-sm'>
          <p>
            <Trans>
              Subscription is active immediately. Share the invoice link with
              the customer to collect the first payment.
            </Trans>
          </p>
          <p>
            <span className='font-medium'>
              <Trans>Invoice:</Trans>
            </span>{' '}
            {result.invoiceId} ·{' '}
            <StatusBadge
              label={
                result.collectionMethodTarget === 'card'
                  ? t`Card target`
                  : t`Bank transfer`
              }
              tone='info'
            />{' '}
            {result.sent ? (
              <StatusBadge label={t`Emailed`} tone='success' />
            ) : (
              <StatusBadge label={t`Not emailed`} tone='default' />
            )}
          </p>
          {hostedInvoiceUrl ? (
            <p className='break-all font-mono text-xs'>{hostedInvoiceUrl}</p>
          ) : (
            <p className='text-muted-foreground'>
              <Trans>
                Hosted invoice link is being generated; refresh the invoices
                list in a moment.
              </Trans>
            </p>
          )}
          {result.hostedInvoiceUrlExpiresAt && (
            <p className='text-muted-foreground'>
              <Trans>
                Link expires around{' '}
                {new Date(
                  result.hostedInvoiceUrlExpiresAt,
                ).toLocaleDateString()}{' '}
                (refresh it from the invoices table if it expires).
              </Trans>
            </p>
          )}
        </div>
      }
      actions={
        <div className='flex flex-wrap gap-2'>
          {hostedInvoiceUrl && (
            <>
              <Button size='sm' onClick={copyLink}>
                <Copy className='mr-1.5 size-4' />
                <Trans>Copy invoice link</Trans>
              </Button>
              <Button
                size='sm'
                variant='secondary'
                onClick={() =>
                  window.open(hostedInvoiceUrl, '_blank', 'noopener,noreferrer')
                }
              >
                <ExternalLink className='mr-1.5 size-4' />
                <Trans>Show invoice</Trans>
              </Button>
            </>
          )}
          <Button size='sm' variant='ghost' onClick={onDismiss}>
            <X className='mr-1.5 size-4' />
            <Trans>Dismiss</Trans>
          </Button>
        </div>
      }
    />
  );
}
